import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { SelectField, TextField } from "@/components/Field";
import { Loaded } from "@/components/Loading";
import { EmptyState } from "@/components/EmptyState";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { call, CALLABLES } from "@/services/callables";
import { formatDate } from "@/lib/format";
import { FIRMWARE_MANIFEST_URL, loadManifest } from "../devices/provisioning/manifest";
import type { FirmwareManifest } from "../devices/provisioning/manifest";
import { KIT_CHANNELS, ROUTER_MODEL_LABEL_KEY, routerChecklist } from "./routers";
import { KIT_NAME_MAX_LENGTH, ROUTER_MODELS } from "@/types";
import type { Kit, RouterModel } from "@/types";

/**
 * ## Kits
 *
 * A kit is a box for one room: a small router with its own Wi-Fi network, a
 * set of devices, a case. The network's name and password are made on the
 * server when the kit is created — nobody chooses them, so no kit ever shares
 * a password with somebody's email — and shown here to whoever sets up the
 * router or joins from the iPad. They are never in a link and never in a mail.
 *
 * Nothing on this page names a person. Kits are furniture.
 */
export function KitsPage() {
  const t = useT();
  const kits = useCallable<{ kits: Kit[] }>(CALLABLES.listKits);
  const [creating, setCreating] = useState(false);

  return (
    <SectionShell
      titleKey="kits.title"
      actions={<Button onClick={() => setCreating(true)}>{t("kits.new")}</Button>}
    >
      <p className="max-w-2xl text-ink-muted">{t("kits.intro")}</p>

      <div className="mt-8">
        <Loaded state={kits}>
          {(loaded) =>
            loaded.kits.length === 0 ? (
              <EmptyState
                titleKey="kits.none"
                bodyKey="kits.noneBody"
                action={<Button onClick={() => setCreating(true)}>{t("kits.new")}</Button>}
              />
            ) : (
              <ul className="flex flex-col gap-6">
                {loaded.kits.map((kit) => (
                  <li key={kit.id}>
                    <KitCard kit={kit} onChanged={kits.reload} />
                  </li>
                ))}
              </ul>
            )
          }
        </Loaded>
      </div>

      <FirmwarePanel />

      <NewKitDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          kits.reload();
        }}
      />
    </SectionShell>
  );
}

/** Sixteen dots for sixteen characters: the shape of the password, not the password. */
const MASK = "•".repeat(16);

/** Copies a value, and says so. */
function useCopy() {
  const { success, error } = useToast();
  return async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      success("kits.copied");
    } catch {
      // A locked-down browser, or no permission. The value is on screen; it
      // can still be read off.
      error("kits.copyFailed");
    }
  };
}

function KitCard({ kit, onChanged }: { kit: Kit; onChanged: () => void }) {
  const t = useT();
  const { locale } = useI18n();
  const { user } = useAuth();
  const { success, showError } = useToast();
  const copy = useCopy();
  const isOwner = user?.role === "owner";
  const [revealed, setRevealed] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [editingNetwork, setEditingNetwork] = useState(false);

  const rotate = async () => {
    try {
      await call(CALLABLES.rotateKitPassword, { kitId: kit.id });
      success("kits.passwordRotated", { name: kit.name });
      onChanged();
    } catch (error) {
      showError(error);
    }
  };

  return (
    <article
      aria-labelledby={`kit-${kit.id}`}
      className="card p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 id={`kit-${kit.id}`} className="section-title">
            {kit.name}
          </h2>
          <p className="mt-1 text-sm text-ink-muted">
            {t("kits.createdOn", { date: formatDate(locale, kit.createdAt) })}
            {" · "}
            {t(ROUTER_MODEL_LABEL_KEY[kit.routerModel])}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setRenaming(true)}>
            {t("kits.rename")}
          </Button>
          <Button variant="secondary" onClick={() => setEditingNetwork(true)}>
            {t("kits.setNetwork")}
          </Button>
          <Button variant="secondary" onClick={() => setRotating(true)}>
            {t("kits.newPassword")}
          </Button>
        </div>
      </div>

      <dl className="mt-5 grid gap-x-8 gap-y-3 sm:grid-cols-[auto_1fr]">
        <dt className="text-sm text-ink-muted">{t("kits.network")}</dt>
        <dd className="flex items-center gap-3">
          <code className="font-mono text-sm text-ink" aria-label={t("kits.network")}>
            {kit.ssid}
          </code>
          <Button variant="ghost" onClick={() => void copy(kit.ssid)}>
            {t("kits.copy")}
          </Button>
        </dd>

        <dt className="text-sm text-ink-muted">{t("kits.password")}</dt>
        <dd className="flex items-center gap-3">
          {/*
            Hidden until asked for. The page is open on a laptop in a room
            with people in it, and a password on screen for the whole session
            is a password on a photograph.
          */}
          <code className="font-mono text-sm text-ink" aria-label={t("kits.password")}>
            {revealed ? kit.psk : MASK}
          </code>
          <Button variant="ghost" onClick={() => setRevealed((shown) => !shown)}>
            {revealed ? t("kits.hidePassword") : t("kits.showPassword")}
          </Button>
          <Button variant="ghost" onClick={() => void copy(kit.psk)}>
            {t("kits.copy")}
          </Button>
        </dd>
      </dl>

      {/*
        The intro promises devices; the card used to show nothing about them
        and offer no way to add one (live run, 2026-09-09). Registering a
        device is the owner's job and happens over USB on another page, so the
        card says that in the two different ways the two roles need to hear it.
      */}
      <div className="mt-5 border-t border-line pt-4">
        <h3 className="field-label">{t("kits.devices")}</h3>
        <p className="text-sm text-ink-muted">
          {t(isOwner ? "kits.devicesOwner" : "kits.devicesTeacher")}
        </p>
        {isOwner ? (
          <Link
            to="/devices/add"
            className="mt-2 inline-block text-sm text-ink-soft underline underline-offset-2 hover:text-ink"
          >
            {t("devices.add.link")}
          </Link>
        ) : null}
      </div>

      <details className="mt-5">
        <summary className="cursor-pointer text-sm text-ink-soft">{t("kits.checklist.title")}</summary>
        <p className="mt-2 text-sm text-ink-muted">{t("kits.checklist.intro")}</p>
        <ol className="mt-3 flex list-decimal flex-col gap-2 pl-5 text-sm text-ink">
          {routerChecklist(kit).map((item) => (
            <li key={item.key}>
              {t(item.key, { channels: KIT_CHANNELS.join(", ") })}
              {item.value ? (
                <>
                  {" "}
                  <code className="font-mono">{item.value}</code>
                </>
              ) : null}
            </li>
          ))}
        </ol>
      </details>

      {/*
        Mounted only while it is open.

        A closed `<dialog>` stays in the document, and this one has fields
        labelled "Wi-Fi name" and "Wi-Fi password" — the same words the card
        itself uses for the values it shows. Two elements with one label is a
        page that cannot be read unambiguously, by a test or by anything else
        that navigates by name.
      */}
      {editingNetwork ? (
        <SetKitNetworkDialog
          kit={kit}
          open
          onClose={() => setEditingNetwork(false)}
          onSaved={() => {
            setEditingNetwork(false);
            onChanged();
          }}
        />
      ) : null}

      <RenameKitDialog
        kit={kit}
        open={renaming}
        onClose={() => setRenaming(false)}
        onRenamed={() => {
          setRenaming(false);
          onChanged();
        }}
      />

      {/*
        Confirmed by typing the kit's name. A new password means every device
        in the box and the router have to be given it again — an afternoon,
        not a click — and the body says so before anyone commits.
      */}
      <ConfirmDialog
        open={rotating}
        onClose={() => setRotating(false)}
        onConfirm={rotate}
        titleKey="kits.rotate.title"
        titleParams={{ name: kit.name }}
        bodyKey="kits.rotate.body"
        expected={kit.name}
        confirmKey="kits.rotate.confirm"
        mismatchKey="kits.rotate.mismatch"
      />
    </article>
  );
}

function NewKitDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const t = useT();
  const { success, showError } = useToast();
  const [name, setName] = useState("");
  const [routerModel, setRouterModel] = useState<RouterModel>("gl-mt3000");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await call(CALLABLES.createKit, { name: name.trim(), routerModel });
      success("kits.created", { name: name.trim() });
      setName("");
      onCreated();
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} titleKey="kits.new">
      <p className="text-sm text-ink-muted">{t("kits.newHelp")}</p>
      <div className="mt-4 flex flex-col gap-4">
        <TextField
          labelKey="kits.field.name"
          hintKey="kits.field.nameHint"
          required
          maxLength={KIT_NAME_MAX_LENGTH}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        {/*
          A select with exactly one option asks a question with one answer
          (live run, 2026-09-09). While there is one router it is stated as a
          fact; the day a second is supported, the field comes back by itself.
        */}
        {ROUTER_MODELS.length > 1 ? (
          <SelectField
            labelKey="kits.field.router"
            hintKey="kits.field.routerHint"
            value={routerModel}
            onChange={(event) => setRouterModel(event.target.value as RouterModel)}
            options={ROUTER_MODELS.map((model) => ({
              value: model,
              labelKey: ROUTER_MODEL_LABEL_KEY[model],
            }))}
          />
        ) : (
          <p className="text-sm text-ink-muted">
            {t("kits.routerOnly", { model: t(ROUTER_MODEL_LABEL_KEY[routerModel]) })}
          </p>
        )}
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button busy={busy} disabled={!name.trim()} onClick={() => void submit()}>
          {t("kits.create")}
        </Button>
      </div>
    </Modal>
  );
}

function RenameKitDialog({
  kit,
  open,
  onClose,
  onRenamed,
}: {
  kit: Kit;
  open: boolean;
  onClose: () => void;
  onRenamed: () => void;
}) {
  const t = useT();
  const { success, showError } = useToast();
  const [name, setName] = useState(kit.name);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await call(CALLABLES.renameKit, { kitId: kit.id, name: name.trim() });
      success("common.saved");
      onRenamed();
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} titleKey="kits.rename">
      {/* The number on every device's label starts with this name; renaming
          the kit does not relabel the cases, and the hint says so. */}
      <p className="text-sm text-ink-muted">{t("kits.renameHelp")}</p>
      <div className="mt-4">
        <TextField
          labelKey="kits.field.name"
          required
          maxLength={KIT_NAME_MAX_LENGTH}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button busy={busy} disabled={!name.trim()} onClick={() => void submit()}>
          {t("common.save")}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * The kit's network, typed in.
 *
 * The generated name and password stay the default, and they are the right
 * one: nobody should be inventing a password and no two kits should share one.
 * But a room that already has a router working — a studio's own, that the
 * building depends on — should not have to be reconfigured to use ours. So the
 * two fields can be set to whatever that router already says (founder,
 * 2026-09-12).
 *
 * It costs the same as a new password, and says so: every device already
 * provisioned holds the old network and has to be given this one over USB.
 */
function SetKitNetworkDialog({
  kit,
  open,
  onClose,
  onSaved,
}: {
  kit: Kit;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const { success, showError } = useToast();
  const [ssid, setSsid] = useState(kit.ssid);
  const [psk, setPsk] = useState(kit.psk);
  const [busy, setBusy] = useState(false);

  // Opening the dialog on a kit whose network changed underneath shows what it
  // is now, not what it was when the page loaded.
  useEffect(() => {
    if (open) {
      setSsid(kit.ssid);
      setPsk(kit.psk);
    }
  }, [open, kit.ssid, kit.psk]);

  const changed = ssid.trim() !== kit.ssid || psk !== kit.psk;
  // WPA2's own limits. A pair outside them is a network no device could join.
  const valid = ssid.trim().length > 0 && psk.length >= 8 && psk.length <= 63;

  const submit = async () => {
    setBusy(true);
    try {
      await call(CALLABLES.setKitNetwork, { kitId: kit.id, ssid: ssid.trim(), psk });
      success("kits.network.saved", { name: kit.name });
      onSaved();
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} titleKey="kits.setNetwork">
      <p className="text-sm text-ink-muted">{t("kits.network.help")}</p>
      <div className="mt-4 flex flex-col gap-4">
        <TextField
          labelKey="kits.field.ssid"
          required
          maxLength={32}
          value={ssid}
          onChange={(event) => setSsid(event.target.value)}
        />
        <TextField
          labelKey="kits.field.psk"
          required
          maxLength={63}
          value={psk}
          onChange={(event) => setPsk(event.target.value)}
        />
      </div>
      {changed ? (
        <p role="status" className="mt-4 text-sm text-ink-soft">
          {t("kits.network.consequence")}
        </p>
      ) : null}
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button busy={busy} disabled={!valid || !changed} onClick={() => void submit()}>
          {t("common.save")}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Which firmware a device added today would get.
 *
 * Read from the manifest this deployment serves, so the version shown is the
 * version flashed. The link is for the person who wants to see the file.
 */
function FirmwarePanel() {
  const t = useT();
  const [manifest, setManifest] = useState<FirmwareManifest | "failed" | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    loadManifest()
      .then((loaded) => {
        if (!cancelled) setManifest(loaded);
      })
      .catch(() => {
        if (!cancelled) setManifest("failed");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section aria-labelledby="firmware-heading" className="mt-10 border-t border-line pt-6">
      <h2 id="firmware-heading" className="section-title">
        {t("kits.firmware.title")}
      </h2>
      <p className="mt-2 text-sm text-ink-muted">
        {manifest === undefined
          ? t("common.loading")
          : manifest === "failed"
            ? t("kits.firmware.unavailable")
            : t("kits.firmware.version", { version: manifest.version })}
      </p>
      {manifest !== undefined && manifest !== "failed" && manifest.note ? (
        <p className="mt-2 text-sm text-danger">{t("kits.firmware.placeholder")}</p>
      ) : null}
      {manifest !== undefined && manifest !== "failed" ? (
        <p className="mt-2 max-w-2xl text-sm text-ink-muted">
          {t("kits.firmware.nothingToInstall")}
        </p>
      ) : null}
      {/*
        The manifest is a file of chip families and byte offsets, and it used
        to replace the page in the same tab, leaving the browser's Back button
        as the only way out (live run, 2026-09-09). It is named for the person
        it is for, and it leaves this page where it was.
      */}
      <a
        href={FIRMWARE_MANIFEST_URL}
        target="_blank"
        rel="noreferrer"
        className="mt-2 inline-block text-sm text-ink-soft underline hover:text-ink"
      >
        {t("kits.firmware.manifestLink")}{" "}
        <span className="sr-only">{t("common.opensInNewTab")}</span>
        <span aria-hidden="true" className="ml-1 text-ink-faint">↗</span>
      </a>
    </section>
  );
}
